// What an operator reads for an audit event's action (#1680): a label per
// action, with the action's own name kept beside it on the screen.
//
// **Why the labels live here and not in the contract.** `AuditEvent.action` is
// what a log, an integrator and a support engineer key on, and its names do not
// change (#1680's Out). A label is copy: it follows web/COPY.md, it is reworded
// by a copy slice, and an integrator has its own words for its own screens. A
// `/v1` field carrying English would be one more thing an integrator keys on
// and one the copy pass could no longer change without a contract change.
//
// **Why the map is complete.** The contract declares `action` a free string, so
// it enumerates nothing. `tools/auditmirror` reads every action the Go source
// can write into `api/auditActions.gen.ts`, generate-check fails CI when that
// file is stale, and this map is a `Record` over its union, so an action with no
// label fails `npm run typecheck`. An action the source no longer writes, on a
// row an older build wrote, has no label and reads as its own name: the screen
// never renders it blank.
//
// The words follow web/COPY.md: the controller, never the cell; *claim*; *held*;
// *declared*; *delivered* for a push the controller took; *the site's router*
// for a gateway; no product name, capability name or decision number.
import type { AuditAction } from "../api/auditActions.gen";
import { fillTerms, term, type LexiconKey, type TermOptions } from "../lexicon";

export const AUDIT_ACTION_LABEL: Record<AuditAction, string> = {
  "ai.briefing": "Briefing requested",
  "apply.window.cancel": "Apply’s confirmation no longer asked",
  "apply.window.confirm": "Apply confirmed as working",
  "ai.diagnose": "Device diagnosis requested",

  "auth.break_glass": "Break-glass session used",
  "auth.break_glass.issue": "Break-glass link issued",
  "auth.login": "Signed in",
  "auth.logout": "Signed out",
  "auth.password_reset": "Password reset by mailed link",
  "auth.password_reset.request": "Password reset link sent",

  // The catalog a site is classified by (ADR-0486): the screen says *Chain*
  // and *Brand*, never *catalogue brand*, and a staff act is the arpIQ team's.
  "catalogue.chain.create": "Chain created",
  "catalogue.chain.update": "Chain changed",
  "catalogue.chain.delete": "Chain deleted",
  "catalogue.chain.promote": "Chain made global",
  "catalogue.chain.merge": "Chain merged into another",
  "catalogue.brand.create": "Brand created",
  "catalogue.brand.update": "Brand changed",
  "catalogue.brand.delete": "Brand deleted",
  "catalogue.brand.promote": "Brand made global",
  "catalogue.brand.merge": "Brand merged into another",

  "auth_method_attachment.request": "Sign-in method attachment requested",
  "auth_method_attachment.attached": "Sign-in method attached",
  "auth_method_attachment.failed": "Sign-in method attachment failed",
  "auth_method_attachment.detach": "Sign-in method detachment started",
  "auth_method_attachment.detaching": "Sign-in method detaching",
  "auth_method_attachment.detached": "Sign-in method detached",
  "authmethod.create": "Sign-in method created",
  "authmethod.disable": "Sign-in method disabled",
  "authmethod.enable": "Sign-in method enabled",
  "authmethod.repoint": "Staff sign-in method repaired",
  "authmethod.rotate_secret": "Sign-in method secret rotated",
  "authmethod.verify": "Sign-in method verified",

  bootstrap: "First organization and superuser created",
  "brand.host.put": "Host name bound to an organization",
  "brand.host.delete": "Host name binding removed",

  "cell.announce": "Site announced to the placement service",
  "cell.register": "Site's controller registered by hand",
  "cell.remove": "Site's controller registration removed",

  // A site's configuration plan, and the rows its apply writes as it goes.
  "config.apply": "Site configuration plan applied",
  "config.apply.accepted": "Site configuration delivered to the controller",
  "config.apply.device_accepted": "Access point's own configuration delivered to the controller",
  "config.apply.placed": "Where each network is in the configuration recorded",
  "config.apply.push": "Telling devices to fetch the site configuration",
  "config.apply.pushed": "Devices told to fetch the site configuration",
  "config.apply.device_pushed": "Access point told to fetch its own configuration",
  "config.apply.push_failed": "Telling devices to fetch the site configuration failed",
  "config.apply.stopped": "Applying the site configuration plan stopped part way",
  "config.apply.taken_after_not_taken": "Controller took the site configuration after not taking it",
  "config.device_apply": "Access point configuration plan applied",
  "config.device_apply.accepted": "Access point configuration delivered to the controller",
  "config.device_apply.push": "Telling the access point to fetch its configuration",
  "config.device_apply.pushed": "Access point told to fetch its configuration",
  "config.device_apply.push_failed": "Telling the access point to fetch its configuration failed",
  "config.device_apply.stopped": "Applying the access point configuration plan stopped part way",
  "config.intent_snapshot.expire": "Restore points past their keeping time deleted",
  "config.restore": "Target restored to an earlier apply's intent, nothing sent",
  "config.reapply": "Device told again to fetch the applied configuration",
  "config.reconcile": "Reconciliation plan applied",
  "config.reconcile.accepted": "Reconciled configuration delivered to the controller",
  "config.reconcile.device_accepted": "Access point's own reconciled configuration delivered to the controller",
  "config.reconcile.device_protected": "Access point kept on its carried configuration",
  "config.reconcile.placed": "Where each network is in the reconciled configuration recorded",
  "config.reconcile.push": "Telling devices to fetch the reconciled configuration",
  "config.reconcile.pushed": "Devices told to fetch the reconciled configuration",
  "config.reconcile.device_pushed": "Access point told to fetch its own reconciled configuration",
  "config.reconcile.push_failed": "Telling devices to fetch the reconciled configuration failed",
  "config.reconcile.stopped": "Applying the reconciliation plan stopped part way",
  "config.reconcile.converged": "Access point reconciled and no longer carried",

  "customer.create": "Customer created",
  "customer.update": "Customer updated",
  "customer.delete": "Customer deleted",

  "dashboard.layout.put": "Home layout saved",
  "dashboard.layout.reset": "Home layout reset",
  "delegation.create": "Handed over to the RF planner",

  "device.claim": "Device claimed",
  "device.unclaim": "Device unclaimed",
  "device.type_suggestion": "Device type suggestion read",
  "device.unclaimed_lookup": "Unclaimed device looked up",
  "device.update": "Device updated",
  "device.import": "Devices imported from a file",
  "device.blink": "Device LEDs flashed",
  "device.reboot": "Device rebooted",
  "device.factory": "Device factory reset",
  "device.firmware_request": "Firmware requested",
  "device.carried_firmware_restated": "Carried firmware restated",
  "device.firmware_delivery": "Firmware upgrade approved",
  "device.firmware_delivery_outcome": "Firmware upgrade outcome",
  "device.leds.set": "Access point LEDs set",
  "device.poe.cycle": "PoE power cycle requested",
  "device.poe.cycle.result": "PoE power cycle finished",
  "device.ports.give": "Port template given to access points",
  "device.ports.replace": "Access point ports' planned settings replaced",
  "device.carried.apply": "Carried configuration applied to an access point",
  "device.carried.apply.complete": "Applying the carried configuration finished",
  "device.push.resolve": "Delivery to the device resolved from the controller's report",
  "device.push.fail": "Delivery to the device recorded as failed",
  "device.cli.read": "Switch's running configuration read",
  "device.console.open": "Console opened",
  "device.console.login": "Signed in at the console",
  "device.console.close": "Console closed",
  "device.console.open.unclaimed": "Console opened on an unclaimed device",
  "device.console.close.unclaimed": "Console on an unclaimed device closed",
  "device.script.read.unclaimed": "Read run on an unclaimed device",
  "device.ssid_ceiling.read": "SSID ceiling read from the device",
  "device.cds_record.read": "CDS record read again",
  "device.cds_transfer.request": "CDS transfer requested",
  "device.cds_transfer.approve": "CDS transfer approved",
  "device.cds_transfer.release": "Held CDS transfer released",
  "device.diagnostics_read": "Device diagnostics read",
  "device.command_document_read": "Configuration a command sent read",
  "device.command_documents_compared": "Configurations commands sent compared",
  "device.held_document_read": "Held configuration read",

  "identity.link": "Sign-in identity linked",
  "identity.unlink": "Sign-in identity unlinked",
  "identity.link_claim.issue": "Identity link issued",
  "identity.link_claim.spend": "Identity link used",

  "invitation.create": "Invitation created",
  "invitation.accept": "Invitation accepted",
  "invitation.revoke": "Invitation revoked",
  "invitation.host": "Account created at the identity provider for an invitation",

  "link.create": "Link declared",
  "link.update": "Link updated",
  "link.delete": "Link withdrawn",

  "location.create": "Location added",
  "location.create_batch": "Locations added",
  "location.update": "Location updated",
  "location.delete": "Location removed",

  "membership.create": "Role granted",
  "membership.delete": "Role revoked",

  "network.create": "Network created",
  "network.update": "Network updated",
  "network.delete": "Network deleted",
  "network.reach.replace": "Access points a network reaches set",
  "network.material_reveal": "Network key revealed",
  "network_template.create": "Network template created",
  "network_template.delete": "Network template deleted",
  "network_template.give": "Network template given to a site",
  "network_template.update": "Network template changed",

  "org.create": "Organization created",
  "org.update": "Organization updated",
  "org.delete": "Organization deleted",
  "org.brand.logo.put": "Brand logo uploaded",
  "org.brand.logo.delete": "Brand logo removed",
  "org.brand.logo_dark.put": "Brand dark logo uploaded",
  "org.brand.logo_dark.delete": "Brand dark logo removed",
  "org.brand.favicon.put": "Brand favicon uploaded",
  "org.brand.favicon.delete": "Brand favicon removed",

  // The lexicon's words (ADR-0477 1a–3a) are written as a key in braces —
  // `{Unit}`, `{units}`, `{Portal}`, `{visitors}` — and filled by
  // auditActionLabel with the screen's own word, neutral where it has none.
  "pan.create": "{Unit} created",
  "pan.update": "{Unit} updated",
  "pan.delete": "{Unit} deleted",
  "pan.import": "{Units} imported from a file",
  "pan.passphrase.create": "{Unit} passphrase added",
  "pan.passphrase.delete": "{Unit} passphrase removed",
  "pan.material_reveal": "{Unit} passphrase revealed",
  "pan.sweep": "Expired {units} marked expired",

  "planned_device.create": "Planned device added",
  "planned_device.update": "Planned device updated",
  "planned_device.delete": "Planned device removed",
  "planned_device.bind": "Serial bound to a planned device",
  "planned_device.unbind": "Serial unbound from a planned device",
  "planned_device.enrol": "Planned devices enrolled",
  "planned_device.ports.replace": "Planned switch ports' settings replaced",

  "port_template.create": "Port template created",
  "port_template.update": "Port template updated",
  "port_template.delete": "Port template deleted",
  "port_template.give": "Port template given",

  "portal.create": "{Portal} created",
  "portal.update": "{Portal} updated",
  "portal.delete": "{Portal} deleted",
  "portal.rotate_secrets": "{Portal} secrets rotated",
  "portal.guests.export": "{Portal} {visitors} exported",
  "portal.network.bind": "Network bound to {portal}",
  "portal.network.unbind": "Network unbound from {portal}",
  "portal.voucher_key.create": "Voucher key created",
  "portal.voucher.mint": "Vouchers minted",
  "portal.voucher.revoke": "Voucher revoked",

  "provider_org.request": "Organization at the identity provider requested",
  "provider_org.created": "Organization at the identity provider created",
  "provider_org.failed": "Organization at the identity provider not created",

  "radio.replace": "Site radio settings replaced",
  "report.classification.export": "Classification report exported",
  "report.send": "Bug report or feature request sent to support",
  "radio.overlay.replace": "Access point radio settings replaced",

  "serviceprincipal.create": "Service principal created",
  "serviceprincipal.disable": "Service principal disabled",
  "serviceprincipal.token.create": "Service principal token created",
  "serviceprincipal.token.revoke": "Service principal token revoked",

  "site.create": "Site created",
  "site.update": "Site updated",
  "site.delete": "Site deleted",
  "site.former_name_add": "Former name kept",
  "site.former_name_remove": "Former name removed",
  "site.place.refresh": "Site position refreshed from its map place",
  "site.place.expire": "Site position removed after 30 days without a refresh",
  "site.upgrade_run.start": "Site upgrade run approved",
  "site.upgrade_run.end": "Site upgrade run ended",
  "site.withdraw": "Deleted site withdrawn from the placement service",
  "site.provision_venue": "Site provisioned on the controller",
  "site.password_reveal": "Site administrative password revealed",
  "site.password_backfill": "Site administrative password generated",
  "site.ssh_key_add": "SSH key added",
  "site.ssh_key_remove": "SSH key removed",
  "site.generated_ssh_key_reveal": "Site\u2019s own SSH private key revealed",
  "site.generated_ssh_key_rotate": "Site\u2019s own SSH key rotated",
  "site.generated_ssh_key_remove": "Site\u2019s own SSH key removed",
  "site.generated_ssh_key_backfill": "Site\u2019s own SSH key generated",
  "site.management_vlan.set": "Management VLAN declared",
  "site.management_vlan.clear": "Management VLAN declaration cleared",
  "site.arpiq_lab.mark": "Marked as a platform lab site",
  "site.arpiq_lab.clear": "Platform lab site mark cleared",
  "site.work_list.apply": "Work list applied",
  "site.config_source.forget": "Stranded configuration record forgotten",
  "site.carryover": "Carry-over import committed",
  "site.carryover.complete": "Carry-over import finished",
  "site.cutover.request": "Site redirect requested",
  "site.carried.seed": "Imported configurations seeded to the controller",
  "site.carried.seed.complete": "Seeding imported configurations finished",
  // Gateway here is the site's router (web/COPY.md's glossary).
  "site.gateway_enrol": "Site router enrolled",
  "site.gateway_credential_set": "Site router credential set",
  "site.gateway_apply": "Site router plan applied",
  "site.gateway_apply.applied": "Site router plan finished applying",
  "site.gateway_apply.failed": "Site router plan failed to apply",
  "site.gateway_apply.stopped": "Applying the site router plan stopped part way",

  "stock.add": "Serial held in a customer's warehouse",
  "stock.remove": "Serial removed from a customer's warehouse",

  "switch.cli_setting.apply": "Switch CLI setting sent",
  "switch.cli_setting.apply.complete": "Sending the switch CLI setting finished",
  "switch.config.apply": "Switch configuration plan applied",
  "switch.config.apply.complete": "Applying the switch configuration plan finished",
  "switch.config.apply.push_failed": "Switch configuration not delivered to the controller",
  "switch.config.apply.push_unknown": "Whether the switch configuration was delivered is unknown",
  "switch.first_document.apply": "Switch's first configuration applied",
  "switch.first_document.apply.complete": "Applying the switch's first configuration finished",
  "switch.release": "Switch released from carried",
  "switch.ports.import": "Switch ports imported from its running configuration",
  "switch.ports.replace": "Switch ports' planned settings replaced",

  "user.create": "User created",
  "user.update": "User updated",
  "user.password_set": "User password set",
  "user.superuser.grant": "Superuser granted",
  "user.superuser.revoke": "Superuser revoked",

  "vlan.removal": "VLAN removed from the site's planned settings",
  "vlan_name.put": "VLAN name set",
  "way_out.replace": "WAN circuits replaced",
};

/** The label for an action, or undefined where this build has none: a row an
 * older build wrote may carry an action nothing writes now, and the screen then
 * shows the action's own name alone. */
export function auditActionLabel(action: string, word: (key: LexiconKey, opts?: TermOptions) => string = (k, o) => term(undefined, k, o)): string | undefined {
  if (!Object.hasOwn(AUDIT_ACTION_LABEL, action)) return undefined;
  // A label names a lexicon word by its key in braces, so the audit list speaks
  // the screen's vertical (ADR-0477 3a, "audit labels"). Every other label is
  // returned as written.
  return fillTerms(AUDIT_ACTION_LABEL[action as AuditAction], word);
}

// What an audit row's target and actor are, in words (UX review 2026-11-01,
// UX-003): the screen says "Site Vieux-Port", never "site 53b5db59". The
// contract's `target_type` is a free string, so this map is partial: a type
// with no word here reads as its own name, as an action with no label does.
// The lexicon's words are written as a key in braces, as the action labels are.
export const AUDIT_TARGET_LABEL: Record<string, string> = {
  org: "Organization",
  customer: "Customer",
  site: "{Site}",
  device: "Device",
  switch: "Switch",
  user: "Person",
  network: "Wireless network",
  pan: "{Pan}",
  portal: "{Portal}",
  invitation: "Invitation",
  membership: "Role",
  link: "Link",
  location: "Location",
  planned_device: "Planned device",
  port_template: "Port template",
  network_template: "Network template",
  generated_config: "Site configuration",
  device_config_source: "Device configuration source",
  site_cell: "Site's controller",
  auth_method: "Sign-in method",
  auth_method_attachment: "Sign-in method attachment",
  identity: "Sign-in identity",
  service_principal: "Service principal",
  service_principal_token: "Service principal token",
  provider_org: "Provider",
  chain: "Chain",
  catalogue_brand: "Brand",
  brand_host: "Host name",
  messaging_provider: "Mail provider",
  voucher: "Voucher",
  import: "Import",
  intent_snapshot: "Design snapshot",
  reapply_attempt: "Re-apply attempt",
  report: "Report",
};

/** The word for a target type, or the type itself where this build has none. */
export function auditTargetLabel(type: string, word: (key: LexiconKey, opts?: TermOptions) => string = (k, o) => term(undefined, k, o)): string {
  if (!Object.hasOwn(AUDIT_TARGET_LABEL, type)) return type;
  return fillTerms(AUDIT_TARGET_LABEL[type]!, word);
}

/** Who acted, as a word, where the server sends no name. */
export const AUDIT_ACTOR_LABEL: Record<string, string> = {
  user: "Person",
  token: "API token",
  system: "System",
};
